import React from 'react';
import styled from 'styled-components';
import SearchInputWithChips from './SearchInputWithChips';
import i18n from '../i18n';

/**
 * AdditionalPreferencesSection Component - Collapsible section for preferred/excluded interpreters
 *
 * @param {Object} props
 * @param {boolean} [props.expanded=false] - Whether the section is expanded
 * @param {Function} props.onToggle - Callback when toggle button clicked
 * @param {Array<{id: string, name: string}>} props.preferredInterpreters - Array of preferred interpreters
 * @param {Array<{id: string, name: string}>} props.excludedInterpreters - Array of excluded interpreters
 * @param {Function} props.onSearchPreferred - Callback when searching for preferred (query)
 * @param {Function} props.onSelectPreferred - Callback when preferred interpreter selected ({id, name})
 * @param {Function} props.onRemovePreferred - Callback when preferred interpreter removed (id)
 * @param {Function} props.onSearchExcluded - Callback when searching for excluded (query)
 * @param {Function} props.onSelectExcluded - Callback when excluded interpreter selected ({id, name})
 * @param {Function} props.onRemoveExcluded - Callback when excluded interpreter removed (id)
 * @param {Array<{id: string, name: string}>} [props.preferredSearchResults=[]] - Search results for preferred
 * @param {Array<{id: string, name: string}>} [props.excludedSearchResults=[]] - Search results for excluded
 * @param {boolean} [props.preferredLoading=false] - Loading state for preferred search
 * @param {boolean} [props.excludedLoading=false] - Loading state for excluded search
 * @param {string} [props.preferredError] - Error message for preferred field
 * @param {string} [props.excludedError] - Error message for excluded field
 * @param {string} [props.className] - Additional CSS class
 */
const AdditionalPreferencesSection = ({
  expanded = false,
  onToggle,
  preferredInterpreters = [],
  excludedInterpreters = [],
  onSearchPreferred,
  onSelectPreferred,
  onRemovePreferred,
  onSearchExcluded,
  onSelectExcluded,
  onRemoveExcluded,
  preferredSearchResults = [],
  excludedSearchResults = [],
  preferredLoading = false,
  excludedLoading = false,
  preferredError,
  excludedError,
  className,
}) => {
  const handleToggle = () => {
    if (onToggle) {
      onToggle();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    } else if (e.key === 'Escape' && expanded) {
      handleToggle();
    }
  };

  return (
    <SectionContainer className={className}>
      <Header>
        <HeaderTitle>
          {i18n('preferences_additional_preferences', 'Additional preferences')}
        </HeaderTitle>

        <ToggleButton
          type="button"
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          aria-expanded={expanded}
          aria-controls="additional-prefs-content"
        >
          <ButtonText>
            {expanded
              ? i18n('hide', 'Hide')
              : i18n('open', 'Open')}
          </ButtonText>
          <ExpandIcon $expanded={expanded} aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d={expanded
                  ? 'M5 12.5L10 7.5L15 12.5'
                  : 'M5 7.5L10 12.5L15 7.5'}
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </ExpandIcon>
        </ToggleButton>
      </Header>

      <Content
        id="additional-prefs-content"
        $expanded={expanded}
        aria-hidden={!expanded}
      >
        <ContentInner>
          <SearchInputWithChips
            id="preferred-interpreter"
            label={i18n('preferences_preferred_interpreter', 'Request preferred interpreter')}
            placeholder={i18n('preferences_search_placeholder', 'Search interpreter by name...')}
            selectedItems={preferredInterpreters}
            onSearch={onSearchPreferred}
            onSelect={onSelectPreferred}
            onRemove={onRemovePreferred}
            searchResults={preferredSearchResults}
            loading={preferredLoading}
            error={preferredError}
          />

          <SearchInputWithChips
            id="excluded-interpreter"
            label={i18n('preferences_excluded_interpreter', 'Exclude specific interpreter(s)')}
            placeholder={i18n('preferences_search_placeholder', 'Search interpreter by name...')}
            selectedItems={excludedInterpreters}
            onSearch={onSearchExcluded}
            onSelect={onSelectExcluded}
            onRemove={onRemoveExcluded}
            searchResults={excludedSearchResults}
            loading={excludedLoading}
            error={excludedError}
          />
        </ContentInner>
      </Content>
    </SectionContainer>
  );
};

const SectionContainer = styled.div`
  border: 1px solid rgba(69, 68, 107, 0.2);
  border-radius: 8px;
  margin-top: 24px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  cursor: pointer;
  user-select: none;
`;

const HeaderTitle = styled.h3`
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0.5px;
  color: #45446b;
  margin: 0;
  padding: 0;
`;

const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  background: transparent;
  border: 1px solid rgba(69, 68, 107, 0.2);
  border-radius: 50px;
  cursor: pointer;
  outline: none;
  user-select: none;
  transition: background-color 150ms ease-in-out;

  &:hover {
    background: rgba(69, 68, 107, 0.05);
    border-color: rgba(69, 68, 107, 0.6);
  }

  &:focus-visible {
    outline: 2px solid #45446b;
    outline-offset: 2px;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ButtonText = styled.span`
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 1.2;
  letter-spacing: 0.15px;
  color: #45446b;
  user-select: none;
`;

const ExpandIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: #45446b;
  transition: transform 250ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${props => props.$expanded ? 'rotate(0deg)' : 'rotate(0deg)'};
`;

const Content = styled.div`
  max-height: ${props => props.$expanded ? '500px' : '0'};
  opacity: ${props => props.$expanded ? '1' : '0'};
  transition:
    max-height ${props => props.$expanded ? '300ms' : '250ms'}
    ${props => props.$expanded ? 'cubic-bezier(0.4, 0, 0.2, 1)' : 'cubic-bezier(0.4, 0, 0.2, 1)'},
    opacity ${props => props.$expanded ? '300ms' : '250ms'}
    ${props => props.$expanded ? 'cubic-bezier(0.4, 0, 0.2, 1)' : 'cubic-bezier(0.4, 0, 0.2, 1)'};
  visibility: ${props => props.$expanded ? 'visible' : 'hidden'};
`;

const ContentInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`;

export default AdditionalPreferencesSection;
